/* eslint-disable max-lines */
import { signInWithRedirect } from 'aws-amplify/auth'
import { getCachedAuthSession } from 'services/authSessionCache'
import Environment from 'helpers/environment'
import ApiError from 'services/ApiError'

import { AdminNotificationsPolicy } from 'types/AdminNotificationsPolicy'
import { ArchivedDevice } from 'types/ArchivedDevice'
import { Customer } from 'types/Customer'
import { Device } from 'types/Device'
import { ModifiedDeviceCreatePayload } from 'types/ModifiedDeviceCreatePayload'
import { ModifiedDeviceUpdateServiceContractPayload } from 'types/ModifiedDeviceUpdateServiceContractPayload'
import { ModifiedSystemCreatePayload } from 'types/ModifiedSystemCreatePayload'
import { MutedNotificationsUpdatePayload } from 'types/MutedNotificationsUpdatePayload'
import { ResourceRequest, ValidResourceTypes } from 'types/Resource'
import { SystemComponent } from 'types/SystemComponent'
import { SystemConfiguration } from 'types/SystemConfigReport'
import { SystemReportData } from 'types/SystemReportData'
import { BillingData } from 'types/BillingData'
import { ZendeskTicketPayload } from 'types/Zendesk'

import AmplifyLoginHelper from 'helpers/AmplifyLoginHelper'

import FeatureTourAttributes from 'types/FeatureTour'
import { HehmDeviceEquipment } from 'types/HehmDevice'
import SiteMessageAttributes from 'types/SiteMessage'
import { Metric as DeviceMetricAttributes, Trend } from 'types/DeviceMetric'
import { NotificationRecipient } from 'types/NotificationRecipient'
import { AlertDescription } from 'types/Alert'
import { DealerOptions } from 'hooks/useDealers'

let serverErrorFn: (errorMessage: string) => void
export const setServerErrorFn = (fn: (errorMessage: string) => void) => {
  serverErrorFn = fn
}

type Props = {
  path: string
  method?: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE'
  payload?: any // eslint-disable-line
  showBannerError?: boolean
  fetchOptions?: { signal: AbortSignal }
  isFile?: boolean
}

const processResponse = (response: Response, showBannerError: boolean, isFile: boolean) => {
  if (response.status >= 500) {
    if (showBannerError) { serverErrorFn('SERVER ERROR') }
    throw new ApiError('Server Error', response)
  } else if (response.status > 401 && response.status < 500) {
    throw new ApiError('Server Error', response)
  } else if (response.status === 401) {
    throw new ApiError('Unauthorized', response)
  } else if (response.status === 400) {
    return Promise.reject(response)
  } else if (response.status === 204) {
    return null
  } else if (response.status === 304) {
    return null
  } else if (isFile && response.status === 200) {
    return response.blob()
  } else {
    return response.json()
  }
}

const fetchFromApi = async ({ path, method = 'GET', payload = null, showBannerError = true, fetchOptions, isFile = false }: Props) => {
  let session

  if (Environment.isAWSCognito()) {
    session = await getCachedAuthSession()

    if (!session?.tokens?.idToken) {
      AmplifyLoginHelper.handleLanding()

      await signInWithRedirect({ provider: { custom: 'AzureB2C' } })
      return
    }
  }
  const isMultipart = payload instanceof FormData
  const body = isMultipart ? payload : (payload ? JSON.stringify(payload) : null)

  return await fetch(`${Environment.backendApiURL()}/api${path}`, Object.assign({
    method,
    credentials: 'include',
    mode: 'cors',
    headers: {
      ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
      Accept: 'application/json',
      ...(Environment.isAWSCognito() && { 'COGNITO-Authorization': `${session?.tokens?.idToken}` })
    },
    body
  }, fetchOptions) as RequestInit).then((response) => {
    return processResponse(response, showBannerError, isFile)
  })
}

const buildParams = <T extends string | number | boolean>(unformattedParams: Record<string, T> = {}) => {
  const formattedParams = new URLSearchParams()

  Object.keys(unformattedParams).forEach((key) => {
    if (unformattedParams[key] === undefined) { return }

    return formattedParams.append(key, unformattedParams[key] as string)
  })

  return `${formattedParams.toString()}`
}

// Please keep this list alphabetized
const Api = {
  AlertDescriptions: {
    create: (payload: AlertDescription) => fetchFromApi({ path: '/alarm_descriptions', method: 'POST', payload }),
    delete: (code: string) => fetchFromApi({ path: `/alarm_descriptions/${code}`, method: 'DELETE' }),
    list: () => fetchFromApi({ path: '/alarm_descriptions' }),
    update: (payload: AlertDescription) => fetchFromApi({ path: `/alarm_descriptions/${payload.code}`, method: 'PATCH', payload })
  },
  AlertGuide: {
    getPDF: () => fetchFromApi({ path: '/alert_guide/report', isFile: true }),
    getExcel: () => fetchFromApi({ path: '/alert_guide/report/xlsx', isFile: true })
  },
  AlertNotifications: {
    get: (code: string) => fetchFromApi({ path: `/alert_notifications/${code}`, method: 'GET' }),
    list: ({ page = 1, sortAttribute = '', sortDirection = '', query = '' } = {}) => { // TODO: use buildParams method for everything after '?'
      let url = `/alert_notifications?page_size=20&page_number=${page}&sort_option=${sortAttribute?.toUpperCase()}_${sortDirection?.toUpperCase()}`
      if (query) {
        url += `&search_term=${encodeURIComponent(query.trim())}`
      }
      return fetchFromApi({ path: url })
    },
    update: (payload: Partial<AdminNotificationsPolicy>, code: string) => fetchFromApi({ path: `/alarm_notifications/${code}`, method: 'PATCH', payload })
  },
  ArchivedDevices: {
    create: (dealerId: number, payload: Omit<ArchivedDevice, 'id' | 'createdAt'>) => fetchFromApi({ path: `/dealers/${dealerId}/archived_devices`, method: 'POST', payload }),
    delete: (dealerId: number, archivedDeviceId: number) => fetchFromApi({ path: `/dealers/${dealerId}/archived_devices/${archivedDeviceId}`, method: 'DELETE' }),
    list: (dealerId: number) => fetchFromApi({ path: `/dealers/${dealerId}/devices/archived` }),
    update: (dealerId: number, archivedDeviceId: number, payload: ArchivedDevice) => fetchFromApi({ path: `/dealers/${dealerId}/archived_devices/${archivedDeviceId}`, method: 'PATCH', payload })
  },
  AwairEvents: {
    list: (deviceId: string, awairDeviceId: string, date: string) => fetchFromApi({ path: `/devices/${deviceId}/awair_device/${awairDeviceId}/awair_events/${date}` })
  },
  ConsolidatedEvents: {
    get: (deviceId: string, date: string) => fetchFromApi({ path: `/devices/${deviceId}/consolidated_events/${date}` }),
    list: () => fetchFromApi({ path: '/consolidated_events' })
  },
  Customer: {
    create: (dealerId: string, payload: Customer) => fetchFromApi({ path: `/dealers/${dealerId}/customers`, method: 'POST', payload }),
    delete: (dealerId: string, customerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/customers/${customerId}`, method: 'DELETE' }),
    get: (dealerId: string, customerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/customers/${customerId}` }),
    update: (dealerId: string, customerId: string, payload: Customer) => fetchFromApi({ path: `/dealers/${dealerId}/customers/${customerId}`, method: 'PATCH', payload })
  },
  CustomerAdoption: {
    get: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/get_customers_for_customer_adoption` })
  },
  Customers: {
    bulkUpdateFlag: (dealerId: number, payload: { flag_type: string, sync_home: boolean }) => fetchFromApi({ path: `/dealers/${dealerId}/customers/bulk_update_flag`, method: 'POST', payload }),
    criticalAndMajorAlerts: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/customers_with_critical_and_major_alerts`, method: 'GET' }),
    getPiiInfo: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/pii_info/customers`, method: 'GET' }),
    list: (dealerId: string, { page = 1, sortAttribute = '', sortDirection = '', query = '', filters = '', size = '10' } = {}) => { // TODO: use buildParams method for everything after '?'
      let url = `/dealers/${dealerId}/paged/customers?page_size=${size}&page_number=${page}&sort_option=${sortAttribute?.toUpperCase()}_${sortDirection?.toUpperCase()}`
      if (query) {
        url += `&search_term=${encodeURIComponent(query.trim())}`
      }
      if (filters) {
        url += `&filter_string=${encodeURIComponent(filters.trim())}`
      }
      return fetchFromApi({ path: url })
    },
    pinned: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/pinned_customers` }),
    search: (dealerId: string, searchTerm: string | null) => fetchFromApi({ path: `/dealers/${dealerId}/customer_search/${searchTerm}`, method: 'GET' })
  },
  DealerImage: {
    updateImage: (dealerId: number, image: string) => fetchFromApi({ path: `/admin/dealers/${dealerId}/image`, method: 'PATCH', payload: { image } }),
    deleteImage: (dealerId: number) => fetchFromApi({ path: `/admin/dealers/${dealerId}/image`, method: 'DELETE' })
  },
  Dealers: {
    contactInfo: (dealerId: number) => fetchFromApi({ path: `/dealers/${dealerId}/contact_info` }),
    get: (dealerId: number) => fetchFromApi({ path: `/dealers/${dealerId}` }),
    updateCustomerFacingAccount: (dealerId: number, payload: { address?: string, city?: string, state?: string, zip?: string, country?: string, phoneNumber?: string, email?: string }) => fetchFromApi({ path: `/dealers/${dealerId}/customer_facing_account`, method: 'PUT', payload }),
    list: (options: DealerOptions = {}) => { // TODO: use buildParams method for everything after '?'
      const { page = 1, pageSize = 10, query = '', filters = '' } = options
      let url = `/admin/dealers?page_size=${pageSize}&page_number=${page}`
      if (query) {
        url += `&search_term=${encodeURIComponent(query.trim())}`
      }
      if (filters) {
        url += `&filter_string=${encodeURIComponent(filters.trim())}`
      }
      return fetchFromApi({ path: url })
    },
    migrate: (fromDealerId: number, toDealerId: number) => fetchFromApi({ path: `/admin/dealers/migrate/${fromDealerId}/${toDealerId}`, method: 'PUT' }),
    migrateStatus: (jobId: string) => fetchFromApi({ path: `/admin/dealers/migrate/status?job_id=${jobId}` }),
    piiFlags: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/pii_flags` }),
    getDeviceStatus: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/device_status` })
  },
  DefaultFirmwareLoads: {
    get: () => fetchFromApi({ path: '/default_firmware_loads', showBannerError: false })
  },
  Device: {
    create: (payload: ModifiedDeviceCreatePayload | ModifiedSystemCreatePayload) => fetchFromApi({ path: '/device', method: 'POST', payload }),
    delete: (deviceId: string) => fetchFromApi({ path: `/device/${deviceId}`, method: 'DELETE' }),
    get: (dealerId: number, deviceId: string) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}` }),
    migrate: (deviceId: string, fromDealerId: number, toDealerId: number | undefined) => fetchFromApi({ path: `/devices/${deviceId}/migrate/${fromDealerId}/${toDealerId}`, method: 'PUT' }),
    update: (dealerId: number, deviceId: string, payload: Pick<Device, 'note'> | Device | { note: string, noteUpdated?: boolean, deviceType?: string, dispositionAction?: string }) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}`, method: 'PATCH', payload }),
    updateServiceContract: (dealerId: number, deviceId: string, payload: ModifiedDeviceUpdateServiceContractPayload) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}/service_contract`, method: 'PATCH', payload })
  },
  DeviceAggregator: {
    getCustomer: (deviceId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/customer_details` }),
    getCurrentDeviceState: (deviceId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/current_device_state` }),
    getDeviceDetails: (deviceId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/device_details` }),
    getFaceplateMessages: (deviceId: string, startDate: string, endDate: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/faceplate_messages?start_date=${startDate}&end_date=${endDate}` }), // TODO: use buildParams method for everything after '?'
    getFaceplateMessagesCSV: (deviceId: string, params: { startDate: string, endDate: string }) => fetchFromApi({ path: `/admin/devices/${deviceId}/faceplate_messages/csv?${buildParams(params)}`, isFile: true }),
    triggerPastFaceplateMessagesCSV: (deviceId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/faceplate_messages/past/csv/trigger` }),
    getPastFaceplateMessagesCSVStatus: (deviceId: string, jobId: string) => fetchFromApi({ path: `/admin/devices/${deviceId}/faceplate_messages/past/csv/job_status?job_id=${jobId}` })
  },
  Devices: {
    search: (q: string[]) => fetchFromApi({ path: `/devices?q=${q}` })
  },
  DeviceMetrics: {
    create: (payload: DeviceMetricAttributes) => fetchFromApi({ path: '/admin/device_metrics', method: 'POST', payload }),
    list: () => fetchFromApi({ path: '/admin/device_metrics' }),
    get: (id: string) => fetchFromApi({ path: `/admin/device_metrics/${id}/metric` }),
    update: (payload: DeviceMetricAttributes) => fetchFromApi({ path: `/admin/device_metrics/${payload.id}`, method: 'PATCH', payload }),
    delete: (metricId: string) => fetchFromApi({ path: `/admin/device_metrics/${metricId}`, method: 'DELETE' }),
    getDeviceMetrics: (deviceId: string, date: string) => fetchFromApi({ path: `/devices/${deviceId}/device_metrics/${date}` }),
    getDeviceMaintenanceTrends: (deviceId: string) => fetchFromApi({ path: `/devices/${deviceId}/maintenance_trends` }),
    getGroupsList: () => fetchFromApi({ path: '/admin/device_metrics/groups', method: 'GET' }),
    createGroupMetric: (payload: { groupId: string, name: string }) => fetchFromApi({ path: '/admin/device_metrics/groups', method: 'POST', payload }),
    updateGroupMetric: (payload: { groupId: string, name: string }) => fetchFromApi({ path: `/admin/device_metrics/groups/${payload.groupId}`, method: 'PATCH', payload }),
    deleteGroupMetric: (groupId: string) => fetchFromApi({ path: `/admin/device_metrics/groups/${groupId}`, method: 'DELETE' }),
    getTrends: () => fetchFromApi({ path: '/admin/device_metrics/trends', method: 'GET' }),
    getTrendMetricValues: (metricId: string) => fetchFromApi({ path: `/admin/device_metrics/trends/preview?metric_id=${metricId}`, method: 'GET' }),
    createTrend: (payload: Trend) => fetchFromApi({ path: '/admin/device_metrics/trends', method: 'POST', payload }),
    updateTrend: (payload: Trend) => fetchFromApi({ path: `/admin/device_metrics/trends/${payload.id}`, method: 'PATCH', payload }),
    deleteTrend: (id: string) => fetchFromApi({ path: `/admin/device_metrics/trends/${id}`, method: 'DELETE' }),
    sortTrend: (payload: { id: string, sortPosition: number }[]) => fetchFromApi({ path: '/admin/device_metrics/trends/sort', method: 'PUT', payload }),
    getDevicesHasMetrics: () => fetchFromApi({ path: '/admin/device_metrics/has_metrics' })
  },
  DiagnosticsCustomers: {
    update: (dealerId: string, customerId: string, payload: { pinned: boolean }) => fetchFromApi({ path: `/dealers/${dealerId}/diagnostics/customers/${customerId}`, method: 'PATCH', payload })
  },
  Favorite: {
    create: (userId: number, deviceId: string) => fetchFromApi({ path: `/users/${userId}/favorites/${deviceId}`, method: 'POST' }),
    delete: (userId: number, deviceId: string) => fetchFromApi({ path: `/users/${userId}/favorites/${deviceId}`, method: 'DELETE' }),
    list: (userId: number) => fetchFromApi({ path: `/users/${userId}/favorites` })
  },
  FeatureTours: {
    create: (featureTour: Omit<FeatureTourAttributes, 'id'>) => fetchFromApi({ path: '/admin/feature_tours', method: 'POST', payload: featureTour }),
    get: (brand: string) => fetchFromApi({ path: `/admin/feature_tours/${brand}` }),
    list: (brands: string, getPublishedOnly: boolean) => fetchFromApi({ path: `/admin/feature_tours?brands=${brands}&published_only=${getPublishedOnly}` }),
    update: (featureTour: FeatureTourAttributes) => fetchFromApi({ path: `/admin/feature_tours/${featureTour.id}`, method: 'PATCH', payload: featureTour }),
    archive: (featureTourId: Pick<FeatureTourAttributes, 'id'>['id']) => fetchFromApi({ path: `/admin/feature_tours/${featureTourId}/archive`, method: 'PUT' }),
    restore: (featureTourId: Pick<FeatureTourAttributes, 'id'>['id']) => fetchFromApi({ path: `/admin/feature_tours/${featureTourId}/restore`, method: 'PUT' }),
    delete: (featureTourId: Pick<FeatureTourAttributes, 'id'>['id']) => fetchFromApi({ path: `/admin/feature_tours/${featureTourId}`, method: 'DELETE' })
  },
  FeatureTourSiteMessages: {
    list: (featureTourId: number) => fetchFromApi({ path: `/admin/feature_tour_site_messages/${featureTourId}` })
  },
  FeatureTourUserRead: {
    list: (brand: string) => fetchFromApi({ path: `/feature_tour_user_read?brand=${brand}` }),
    create: (payload: { featureTourId: number, brand: string }) => fetchFromApi({ path: '/feature_tour_user_read', method: 'POST', payload })
  },
  HehmDevice: {
    update: (deviceId: string, configuration: HehmDeviceEquipment) => fetchFromApi({ path: `/devices/${deviceId}/hehm_configuration`, method: 'PATCH', payload: configuration })
  },
  HomeHealth: {
    getHehmTermsStatus: () => fetchFromApi({ path: '/home_health/hehm_terms_status' }),
    acceptHehmTerms: () => fetchFromApi({ path: '/home_health/accept_hehm_terms', method: 'POST' }),
    deprovisionDevice: (dealerId: string, deviceId: string, reason: string) => fetchFromApi({ path: `/home_health/deprovision/${dealerId}/${deviceId}/${encodeURIComponent(reason)}`, method: 'DELETE' }),
    getDeprovisionedDevices: (dealerId: string) => fetchFromApi({ path: `/home_health/deprovisioned_devices/${dealerId}` }),
    reprovisionDevice: (dealerId: string, deviceId: string) => fetchFromApi({ path: `/home_health/reprovision/${dealerId}/${deviceId}`, method: 'POST' }),
    assignFreeHehm: (deviceId: string) => fetchFromApi({ path: `/devices/${deviceId}/assign_free_hehm_trial`, method: 'PUT' }),
    deleteFreeTrialHehm: (deviceId: string, type: 'current' | 'assigned') => fetchFromApi({ path: `/devices/${deviceId}/delete_free_trial_hehm_device/${type}`, method: 'DELETE' }),
    getFreeHehmTrials: () => fetchFromApi({ path: '/devices/free_hehm_trials' }),
    getFreeHehmTrialsWithDealers: () => fetchFromApi({ path: '/devices/free_hehm_trials_with_dealers' }),
    getActivityReport: (params: { dealerId?: number, startDate?: string, endDate?: string }) => {
      const queryParams = buildParams(params)
      return fetchFromApi({ path: `/home_health/activity_report?${queryParams}` })
    },
    getDealersWithHehmAccounts: () => fetchFromApi({ path: '/home_health/dealers_with_hehm_accounts' }),
    getLastActivityReportRun: (dealerId?: number) => {
      const queryParams = dealerId ? `?dealerId=${dealerId}` : ''
      return fetchFromApi({ path: `/home_health/last_activity_report_run${queryParams}` })
    },
    getTealReconciliation: () => fetchFromApi({ path: '/home_health/teal_reconciliation' })
  },
  HomeownerAlerts: {
    get: (deviceId: string) => fetchFromApi({ path: `/homeowner_alerts/${deviceId}` })
  },
  MutedNotifications: {
    update: (payload: MutedNotificationsUpdatePayload) => fetchFromApi({ path: '/muted_notifications', method: 'PATCH', payload })
  },
  Notes: {
    create: (dealerId: number, deviceId: string, content: string, noteUpdated?: boolean) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}/note`, method: 'POST', payload: { content, noteUpdated } })
  },
  NotificationRecipients: {
    get: (dealerId: number, notificationRecipientId: number) => fetchFromApi({ path: `/dealers/${dealerId}/notification_recipients/${notificationRecipientId}` }),
    create: (payload: NotificationRecipient) => fetchFromApi({ path: `/dealers/${payload.dealerId}/notification_recipients`, method: 'POST', payload }),
    delete: (dealerId: string | number, notificationRecipientId: string | number) => fetchFromApi({ path: `/dealers/${dealerId}/notification_recipients/${notificationRecipientId}`, method: 'DELETE' }),
    list: (dealerId: number) => fetchFromApi({ path: `/dealers/${dealerId}/notification_recipients` }),
    update: (payload: NotificationRecipient) => fetchFromApi({ path: `/dealers/${payload.dealerId}/notification_recipients/${payload.id}`, method: 'PATCH', payload }),
    verify_email: (payload: { token: string }) => fetchFromApi({ path: `/notification_recipients/verify_email?token=${payload.token}`, method: 'PATCH' })
  },
  Paywall: {
    getPlans: () => fetchFromApi({ path: '/paywall/plans' }),
    getActiveAcct: (dealerId: number, planType: string) => fetchFromApi({ path: `/paywall/active_account/${dealerId}/${planType}` }),
    getPremiumAcct: (dealerId: number) => fetchFromApi({ path: `/paywall/premium_account/${dealerId}` }),
    getAcctPlanChange: (dealerId: number, planType: string) => fetchFromApi({ path: `/paywall/account/plan_change/${dealerId}/${planType}` }),
    getStatements: (dealerId: number, planType: string) => fetchFromApi({ path: `/paywall/statements/${dealerId}/${planType}` }),
    getInvoice: (dealerId: string, accountNo: number, invoiceNo: number) => fetchFromApi({ path: `/paywall/invoice/${dealerId}/${accountNo}/${invoiceNo}` }),
    getHehmQuantity: (dealerId: number) => fetchFromApi({ path: `/paywall/hehm_quantity/${dealerId}` }),
    getOracleAccountInfo: (dealerId: number) => fetchFromApi({ path: `/paywall/oracle_account/${dealerId}` }),
    triggerCybersource: (dealerId: number, planNo: number, content: BillingData) => fetchFromApi({ path: `/paywall/cybersource/${dealerId}/${planNo}`, method: 'POST', payload: { content } }),
    setupTestAccount: (dealerId: number, planNo: number, content: BillingData) => fetchFromApi({ path: `/paywall/test_account/${dealerId}/${planNo}`, method: 'POST', payload: { content } }),
    create: (dealerId: string, planNo: number, content: BillingData) => fetchFromApi({ path: `/paywall/create_account/${dealerId}/${planNo}`, method: 'POST', payload: { content } }),
    updatePlan: (dealerId: number, planNo: number) => fetchFromApi({ path: `/paywall/account_plan/${dealerId}`, method: 'POST', payload: { plan_no: planNo } }),
    cancelPlan: (dealerId: number, planType: string) => fetchFromApi({ path: `/paywall/cancel_plan/${dealerId}/${planType}`, method: 'POST' }),
    calculateTotal: (dealerId: number, planNo: number, content: BillingData) => fetchFromApi({ path: `/paywall/calculate_total/${dealerId}/${planNo}`, method: 'POST', payload: { content } }),
    getDsoHehmInvoiceReport: () => fetchFromApi({ path: '/paywall/dso_hehm_invoice_report' }),
    getDsoHehmInvoiceReportFromCsv: (csvContent: string) => fetchFromApi({ path: '/paywall/dso_hehm_invoice_report_from_csv', method: 'POST', payload: { csvContent } }),
    postExternalPayment: (dealerId: number, acctType: string, amount: string, reference: string) => fetchFromApi({ path: `/paywall/external_payment/${dealerId}`, method: 'POST', payload: { acctType, amount, reference } })
  },
  PremiumAdoption: {
    get: () => fetchFromApi({ path: '/paywall/premium_adoptions' })
  },
  ProductLiterature: {
    download: (pdfUrl: string) => fetchFromApi({ path: `/product_literature/download?pdf_url=${pdfUrl}`, isFile: true }),
    get: (modelNumber: string) => fetchFromApi({ path: `/product_literature?model_number=${modelNumber}` })
  },
  RemoteConfig: {
    compatible: (deviceId: string) => fetchFromApi({ path: `/remote_config/${deviceId}/compatible` }),
    list: (states: string[]) => fetchFromApi({ path: `/remote_config/status?states=${states.join(',')}`, method: 'GET' }), // TODO: use buildParams method for everything after '?'
    requestAccess: (deviceId: string, payload: { duration: number, message: string }) => fetchFromApi({ path: `/remote_config/${deviceId}/request_access?origination=DWA`, method: 'POST', payload }),
    status: (deviceId: string) => fetchFromApi({ path: `/remote_config/${deviceId}/status` }),
    stop: (deviceId: string) => fetchFromApi({ path: `/remote_config/${deviceId}/stop`, method: 'POST' }),
    iframeUrl: (deviceId: string, authToken: string) => Environment.backendApiURL() + `/api/remote_config/${deviceId}/iframe_html?COGNITO_AUTHORIZATION=${authToken}`
  },
  Resources: {
    list: (brand: string, publishedOnly: boolean) => fetchFromApi({ path: `/resources?brand=${brand}&published_only=${publishedOnly}` }),
    update: (resource: ResourceRequest) => fetchFromApi({ path: '/resources', method: 'POST', payload: resource }),
    delete: (id: string, type: ValidResourceTypes) => fetchFromApi({ path: `/resources/${id}?type=${type}`, method: 'DELETE' })
  },
  RuntimeHistory: { // TODO: use buildParams method for everything after '?'
    get: (dealerId: string, deviceId: string, startTime: string, endTime: string, timeZone: string, fetchOptions: { signal: AbortSignal }) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}/runtime_history?start_time=${startTime}&end_time=${endTime}&time_zone=${timeZone}`, method: 'GET', payload: null, showBannerError: true, fetchOptions }),
    getCSV: (dealerId: string, deviceId: string, startTime: string, endTime: string, deviceType: string, timeZone: string) => fetchFromApi({ path: `/dealers/${dealerId}/devices/${deviceId}/csv_runtime_history?start_time=${startTime}&end_time=${endTime}&device_type=${deviceType}&time_zone=${timeZone}`, isFile: true })
  },
  DashboardLayout: {
    get: () => fetchFromApi({ path: '/dashboard_layout' }),
    update: (layout: string) => fetchFromApi({ path: '/dashboard_layout', method: 'PATCH', payload: { dashboardLayout: layout } })
  },
  SearchHistory: {
    modify: (userId: number, type: string, query: string) => fetchFromApi({ path: `/search_history/users/${userId}/${type}`, method: 'POST', payload: { query } }),
    list: (userId: number, type: string) => fetchFromApi({ path: `/search_history/users/${userId}/${type}` })
  },
  Session: {
    get: () => fetchFromApi({ path: '/sessions' }),
    getJwt: () => fetchFromApi({ path: '/sessions/jwt' }),
    update: (payload: { impersonateDealerId?: string | null | undefined, termsUpToDate?: boolean }) => fetchFromApi({ path: '/sessions', method: 'PATCH', payload }),
    logout: (authToken: string) => Environment.backendApiURL() + `/api/sessions/logout?COGNITO_AUTHORIZATION=${authToken}`
  },
  SiteMessageBanners: { // TODO: use buildParams method for everything after '?'
    list: (brands: string, currentMessageLastUpdatedAt: string) => fetchFromApi({ path: `/admin/site_messages?brands=${brands}&filter_by_site_banner_enabled=true&current_message_last_updated_at=${currentMessageLastUpdatedAt || ''}` })
  },
  SiteMessages: {
    create: (siteMessage: SiteMessageAttributes) => fetchFromApi({ path: '/admin/site_messages', method: 'POST', payload: siteMessage }),
    delete: (siteMessageId: string) => fetchFromApi({ path: `/admin/site_messages/${siteMessageId}`, method: 'DELETE' }),
    list: (brands: string, tiers: string, getPublishedOnly: boolean) => fetchFromApi({ path: `/admin/site_messages?brands=${brands}&tiers=${tiers}&published_only=${getPublishedOnly}` }), // TODO: use buildParams method for everything after '?'
    update: (siteMessage: SiteMessageAttributes) => fetchFromApi({ path: `/admin/site_messages/${siteMessage.id}`, method: 'PATCH', payload: siteMessage }),
    archive: (siteMessageId: string) => fetchFromApi({ path: `/admin/site_messages/${siteMessageId}/archive`, method: 'PUT' }),
    restore: (siteMessageId: string) => fetchFromApi({ path: `/admin/site_messages/${siteMessageId}/restore`, method: 'PUT' }),
    availableSiteMessages: (featureTourId: string, brands: string, roles: string, tiers: string) => fetchFromApi({ path: `/admin/site_messages/available?brands=${brands}&roles=${roles}&tiers=${tiers}&feature_tour_id=${featureTourId}` }),
    toursForSiteMessage: (siteMessageId: string) => fetchFromApi({ path: `/admin/feature_tour_site_messages/site_message/${siteMessageId}` })
  },
  SiteMessagesUserRead: {
    create: (payload: { siteMessageIds: number[] }) => fetchFromApi({ path: '/site_messages_user_read', method: 'POST', payload }),
    getUnread: () => fetchFromApi({ path: '/site_messages/unread' }),
    getArchived: () => fetchFromApi({ path: '/site_messages_user_read/archived' }),
    getRemoved: () => fetchFromApi({ path: '/site_messages_user_read/removed' }),
    archive: (siteMessageId: number) => fetchFromApi({ path: `/site_messages_user_read/${siteMessageId}/archive`, method: 'PUT' }),
    remove: (siteMessageId: number) => fetchFromApi({ path: `/site_messages_user_read/${siteMessageId}`, method: 'DELETE' }),
    restore: (siteMessageId: number) => fetchFromApi({ path: `/site_messages_user_read/${siteMessageId}/restore`, method: 'PUT' })
  },
  SystemComponents: {
    create: (deviceId: string, payload: SystemComponent) => fetchFromApi({ path: `/devices/${deviceId}/system_components`, method: 'POST', payload }),
    delete: (deviceId: string, id: number | null) => fetchFromApi({ path: `/devices/${deviceId}/system_components/${id}`, method: 'DELETE' }),
    get: (deviceId: string) => fetchFromApi({ path: `/devices/${deviceId}/system_components` }),
    update: (deviceId: string, id: number, payload: SystemComponent) => fetchFromApi({ path: `/devices/${deviceId}/system_components/${id}`, method: 'PATCH', payload }),
    report: (deviceId: string, params: { filename: string }) => fetchFromApi({ path: `/devices/${deviceId}/system_components/warranty/report?${buildParams(params)}`, isFile: true }),
    getEquipmentImage: (modelNumber: string, showBannerError = false) => {
      return fetchFromApi({ path: `/system_components/images/${modelNumber}`, method: 'GET', payload: null, showBannerError })
    }
  },
  SystemConfig: {
    get: (deviceId: string) => fetchFromApi({ path: `/systems/${deviceId}/configuration` }),
    update: (deviceId: string, configuration: SystemConfiguration) => fetchFromApi({ path: `/systems/${deviceId}/configuration`, method: 'PATCH', payload: configuration })
  },
  SystemReports: {
    list: (deviceId: string) => fetchFromApi({ path: `/system_reports/${deviceId}` }),
    getReport: (params: SystemReportData) => fetchFromApi({ path: `/system_reports?${buildParams(params as unknown as Record<string, string | number | boolean>)}`, isFile: true }),
    uploadReportImage: (image: string, customerId: string) => fetchFromApi({ path: '/system_reports/upload_report_image', method: 'POST', payload: { image, customerId } }),
    sendEmail: (payload: SystemReportData) => fetchFromApi({ path: '/system_reports/send_email', method: 'POST', payload }),
    delete: (deviceId: string, s3FileName: string) => fetchFromApi({ path: `/system_reports/${deviceId}/${encodeURIComponent(s3FileName)}`, method: 'DELETE' })
  },
  UnassignedDevices: {
    list: (dealerId: number) => fetchFromApi({ path: `/dealers/${dealerId}/devices/unassigned` })
  },
  VerificationReports: {
    list: (deviceId: string) => fetchFromApi({ path: `/verification_reports/${deviceId}` })
  },
  WarrantyInfo: {
    get: (serialNumber: string, retrieveCached = false, showBannerError = false) => {
      return fetchFromApi({ path: `/warranty_info/${serialNumber}?retrieve_cached=${retrieveCached}`, method: 'GET', payload: null, showBannerError })
    }
  },
  WistiaVideo: {
    get: (videoId: string) => fetchFromApi({ path: `/wistia_videos/${videoId}` }),
    getFTHVideo: (q: string) => fetchFromApi({ path: `/wistia_videos/fth/videos?search_text=${q}` })
  },
  UnsubscribeEmails: {
    create: (email: string, type: string) => fetchFromApi({ path: `/unsubscribed_emails/unsubscribe?email=${email}&type=${type}`, method: 'POST' }),
    get: (email: string, type: string) => fetchFromApi({ path: `/unsubscribed_emails/unsubscribed?email=${email}&type=${type}` })
  },
  Zendesk: {
    create: (payload: ZendeskTicketPayload) => fetchFromApi({ path: '/zendesk', method: 'POST', payload }),
    upload: (payload: FormData) => fetchFromApi({ path: '/zendesk/upload', method: 'POST', payload })
  },
  RecentDeviceActivity: {
    get: (dealerId: string) => fetchFromApi({ path: `/dealers/${dealerId}/customers/recent_activity` })
  },
  Chatbot: {
    getTermsStatus: () => fetchFromApi({ path: '/chatbot/chatbot_terms_status' }),
    getLatestRevision: () => fetchFromApi({ path: '/chatbot/chatbot_terms_revision' }),
    acceptTerms: () => fetchFromApi({ path: '/chatbot/accept_chatbot_terms', method: 'POST' })
  },
  DailyDigest: {
    list: ({ page, pageSize }: { page: number, pageSize: number } = { page: 1, pageSize: 10 }) => fetchFromApi({ path: `/daily_digest?page_size=${pageSize}&page_number=${page}` })
  }
}

export default Api
